import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { tv } from "tailwind-variants";
import Icon from "../../icons";
import { backgroundSolidActiveVariants, backgroundSolidHoverVariants, backgroundSurfaceActiveVariants, backgroundSurfaceHoverVariants, backgroundSurfaceOpenVariants, borderVariants, containedCompoundVariants, ghostCompoundVariants, outlinedCompoundVariants, surfaceCompoundVariants, textCompoundVariants, textVariants, } from "../../theme/variants/sharedVariants";
import cn from "../../utils/cn";
export const variantKeys = ["surface", "contained", "outlined", "text", "link", "ghost"];
export const colorKeys = ["default", "primary", "success", "danger", "warning", "attention", "purple", "sky", "danger-dark"];
export const sizeKeys = ["regular", "condensed", "md", "sm"];
export const buttonVariants = tv({
    base: cn("flex items-center justify-center whitespace-nowrap", "text-white text-sm font-medium outline-none", "hover:duration-500 hover:ease-in-out", "focus-within:ring-0 focus-within:ring-offset-0 focus:outline-none focus:ring-0", "disabled:cursor-not-allowed disabled:active:scale-100 disabled:pointer-events-none"),
    variants: {
        animation: {
            true: "active:scale-95 active:ring-0 active:transition-all",
            false: "",
        },
        icon: {
            true: "flex flex-row items-center justify-center",
            false: "",
        },
        full: {
            true: "w-full",
            false: "",
        },
        loading: {
            true: "cursor-auto items-center justify-center hover:pointer-events-none",
            false: "",
        },
        disabled: {
            true: "disabled:bg-gray-4 disabled:border-gray-5 text-gray-8 hover:none",
            false: "",
        },
        size: {
            regular: "px-4 py-3 rounded-lg leading-sm h-10",
            condensed: "p-3 py-1.5 rounded-md leading-md h-8",
            md: "px-4 py-3 rounded-lg leading-sm h-10",
            sm: "p-3 py-1.5 rounded-md leading-md h-8",
        },
        color: {
            default: textVariants.default,
            primary: textVariants.primary,
            success: textVariants.success,
            danger: textVariants.danger,
            warning: textVariants.warning,
            attention: textVariants.attention,
            purple: textVariants.purple,
            sky: textVariants.sky,
            "danger-dark": textVariants["danger-dark"],
        },
        variant: {
            contained: "",
            outlined: "border",
            surface: "",
            ghost: "",
            text: "",
            link: "hover:underline",
        },
    },
    compoundVariants: [
        ...containedCompoundVariants.map((item) => ({
            variant: item.variant,
            color: item.color,
            className: cn(item.className, borderVariants[item.color], backgroundSolidHoverVariants[item.color], backgroundSolidActiveVariants[item.color]),
        })),
        ...outlinedCompoundVariants.map((item) => ({
            variant: item.variant,
            color: item.color,
            className: cn(item.className, backgroundSurfaceHoverVariants[item.color], backgroundSurfaceActiveVariants[item.color], backgroundSurfaceOpenVariants[item.color]),
        })),
        ...surfaceCompoundVariants.map((item) => ({
            variant: item.variant,
            color: item.color,
            className: cn(item.className, backgroundSurfaceHoverVariants[item.color], backgroundSurfaceActiveVariants[item.color], backgroundSurfaceOpenVariants[item.color]),
        })),
        ...ghostCompoundVariants.map((item) => ({
            variant: item.variant,
            color: item.color,
            className: cn(item.className, backgroundSurfaceOpenVariants[item.color]),
        })),
        ...textCompoundVariants.map((item) => ({
            variant: item.variant,
            color: item.color,
            className: cn(item.className, backgroundSurfaceOpenVariants[item.color]),
        })),
        {
            icon: true,
            size: ["condensed", "sm"],
            className: "size-8 p-0",
        },
        {
            icon: true,
            size: ["regular", "md"],
            className: "size-10  p-0",
        },
        {
            disabled: true,
            color: colorKeys,
            variant: variantKeys,
            className: "text-gray-8 bg-gray-4 border-gray-5 hover:bg-gray-4 hover:text-none active:bg-gray-4 active:text-gray-8 cursor-not-allowed pointer-events-none active:scale-100",
        },
        {
            disabled: true,
            color: colorKeys,
            variant: ["link", "text"],
            className: "text-gray-8 bg-transparent hover:bg-transparent hover:no-underline active:bg-transparent active:text-gray-8 border-gray-5 hover:text-none cursor-not-allowed pointer-events-none active:scale-100",
        },
    ],
    defaultVariants: {
        size: "regular",
        color: "primary",
        variant: "contained",
        animation: true,
        icon: false,
    },
});
const Button = React.forwardRef(({ variant = "contained", type = "button", size = "regular", animation = true, loading = false, full = false, asChild = false, color, icon, startIcon, endIcon, className, disabled = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const buttonVariantsProps = {
        variant,
        color,
        size,
        icon: !!icon,
        full,
        animation,
        disabled,
    };
    const buttonProps = {
        className: cn(buttonVariants(buttonVariantsProps), className),
        type,
        ref,
        disabled,
        ...props,
    };
    const iconSize = size === "condensed" || size === "sm" ? "!size-4" : "!size-5";
    const iconSizeCanvas = size === "condensed" || size === "sm" ? "sm" : "md";
    const StartIconComp = () => React.cloneElement(startIcon, { className: cn(iconSize, "mr-2"), size: iconSizeCanvas });
    const EndIconComp = () => React.cloneElement(endIcon, { className: cn(iconSize, "ml-2"), size: iconSizeCanvas });
    const IconOnlyComp = () => React.cloneElement(icon, { className: cn(iconSize), size: iconSizeCanvas });
    const LoadingWrapper = () => (_jsx("div", { className: "relative flex h-4 w-12 items-center justify-center", "data-testid": "button-loading", children: _jsx(Icon, { name: "cb-loading-spin", className: cn("absolute ", iconSize) }) }));
    const renderIconAndLoading = () => {
        return (_jsxs(Comp, { ...buttonProps, children: [!loading && startIcon ? _jsx(StartIconComp, {}) : null, loading && !asChild ? _jsx(LoadingWrapper, {}) : children, !loading && endIcon ? endIcon : null] }));
    };
    const renderOnlyLoading = () => {
        return (_jsx(Comp, { ...buttonProps, children: _jsx(LoadingWrapper, {}) }));
    };
    const renderOnlyIcon = () => {
        return _jsx(Comp, { ...buttonProps, children: loading ? _jsx(Icon, { name: "cb-loading-spin" }) : _jsx(IconOnlyComp, {}) });
    };
    if (icon && !children)
        return renderOnlyIcon();
    if (!!startIcon || !!endIcon)
        return renderIconAndLoading();
    if (loading && !asChild)
        return renderOnlyLoading();
    return _jsx(Comp, { ...buttonProps, children: children });
});
Button.displayName = "Button";
export default Button;
